// Response header derivation for site mode and tile mode (szzt-spec.md §9.4, §9.6, §12).
//
// Site mode: true content-type, `nosniff`, immutable content caching, and the publisher's optional
// per-resource CSP. Tile mode: the same verified bytes under a LOCKED header set that overrides any
// publisher CSP and forbids network egress — the tile guarantees are loader-enforced (§9.6).

import { UnsafeHeaderError } from "./types.ts";
import type { ResourceLocation } from "./types.ts";

/** Immutable content caching for CID-addressed responses (§12). */
export const IMMUTABLE_CACHE_CONTROL = "public, max-age=31536000, immutable";

/**
 * True when `value` is safe to hand to `res.writeHead()`. Mirrors Node's own header-value guard
 * (`checkInvalidHeaderChar` in `node:_http_outgoing`), which allows tab and the printable/extended range
 * (0x20-0x7e, 0x80-0xff) and rejects everything else — not just CR/LF, the whole class of control
 * characters. A MASL `content-type`/`content-security-policy` (or, via `routing.ts` `route()`, a routing
 * redirect target) is signed-state a gateway never wrote, so it MUST be checked here before it becomes a
 * header (roadmap H2: an unchecked value crashes the whole process, not just this request).
 */
export function isSafeHeaderValue(value: string): boolean {
  return !/[^\t\x20-\x7e\x80-\xff]/.test(value);
}

/**
 * The locked tile-mode CSP (§9.6). `default-src 'self' blob: data:` forbids network egress from tile
 * code; the loader fetches + verifies, the sandboxed content cannot. Overrides any publisher CSP.
 */
export const TILE_CSP = [
  "default-src 'self' blob: data:",
  "script-src 'self' blob: data: 'unsafe-inline' 'wasm-unsafe-eval'",
  "script-src-attr 'none'",
  "style-src 'self' blob: data: 'unsafe-inline'",
  "form-src 'self'",
  "manifest-src 'none'",
  "object-src 'none'",
  "base-uri 'none'",
  "sandbox allow-downloads allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts",
].join("; ");

/**
 * The locked tile-mode security headers (§9.6). MUST be applied in tile mode and MUST override any
 * publisher-supplied MASL `content-security-policy`. `manifest-src 'none'` + the sandbox preclude
 * install: tile mode is deliberately not a PWA.
 */
export const TILE_HEADERS: Readonly<Record<string, string>> = Object.freeze({
  "content-security-policy": TILE_CSP,
  "cross-origin-opener-policy": "same-origin",
  "cross-origin-resource-policy": "cross-origin",
  "origin-agent-cluster": "?1",
  "permissions-policy": "interest-cohort=(), browsing-topics=()",
  "referrer-policy": "no-referrer",
  "x-content-type-options": "nosniff",
  "x-dns-prefetch-control": "off",
});

/**
 * Site-mode response headers for a resource (§9.4): its true content-type, `x-content-type-options:
 * nosniff`, immutable content caching, and the publisher's optional per-resource CSP.
 */
export function siteHeaders(loc: ResourceLocation): Record<string, string> {
  if (!isSafeHeaderValue(loc.contentType)) throw new UnsafeHeaderError("content-type", loc.contentType);
  if (loc.csp !== undefined && !isSafeHeaderValue(loc.csp)) {
    throw new UnsafeHeaderError("content-security-policy", loc.csp);
  }
  const h: Record<string, string> = {
    "content-type": loc.contentType,
    "x-content-type-options": "nosniff",
    "cache-control": IMMUTABLE_CACHE_CONTROL,
  };
  if (loc.csp !== undefined) h["content-security-policy"] = loc.csp;
  return h;
}

/**
 * Tile-mode response headers for a resource (§9.6): its content-type + immutable caching under the
 * LOCKED header set. The publisher's per-resource CSP is deliberately ignored — {@link TILE_HEADERS}
 * carries the enforced `content-security-policy`, applied last so it always wins.
 */
export function tileHeaders(loc: ResourceLocation): Record<string, string> {
  if (!isSafeHeaderValue(loc.contentType)) throw new UnsafeHeaderError("content-type", loc.contentType);
  return {
    "content-type": loc.contentType,
    "cache-control": IMMUTABLE_CACHE_CONTROL,
    ...TILE_HEADERS,
  };
}
